/**
 * Client-side font conversion pipeline used by the /convert-<from>-to-<to> font
 * pages and the {ext}-converter font hubs.
 *
 * Shape of the pipeline:
 *
 *   input bytes --sniff--> decode to a raw sfnt --> re-flavour only if the
 *   target demands it --> wrap for the target container
 *
 * "sfnt" is the table-directory container that TTF and OTF files are; WOFF,
 * WOFF2 and EOT are wrappers around exactly that. So the decode step unwraps,
 * and the WOFF / WOFF2 / EOT targets simply re-wrap the same sfnt — those
 * conversions are lossless (hinting, GSUB/GPOS, kern and the original outline
 * format all survive). Only a flavour change rebuilds the font:
 *
 *   - CFF (PostScript) -> TrueType, for the TTF target: fonteditor-core's
 *     otf2ttfobject converts the cubic contours to quadratic and writes glyf.
 *   - TrueType -> CFF, for the OTF target: opentype.js re-encodes as CFF.
 *
 * Every heavy library here is behind a dynamic import so that none of them
 * lands in the shared page bundle; they are fetched on the first Convert click
 * and only for the path actually taken.
 */

export type FontFormat = 'ttf' | 'otf' | 'woff' | 'woff2' | 'eot';

/** What the uploaded bytes actually are, regardless of file name or dropdown. */
export type SourceFormat = FontFormat | 'ttc' | 'unknown';

/** Outline format carried by an sfnt: quadratic `glyf` or PostScript `CFF `. */
export type SfntFlavour = 'truetype' | 'cff';

const OTTO = 'OTTO';
/** TrueType sfnt version 0x00010000, plus the two legacy Apple spellings. */
const TRUETYPE_TAGS = ['true', 'typ1'];

/** EOT has no leading magic; its 16-bit 0x504C magic sits at byte 34 (LE). */
const EOT_MAGIC_OFFSET = 34;

/**
 * fonteditor-core read/write options. Both flags are ON deliberately: they keep
 * the TrueType hinting programs (cvt/fpgm/prep/gasp + glyph instructions) and
 * the kerning (kern + GPOS) instead of silently dropping them, which is what a
 * "convert my font" tool is expected to do. — 2026-09-01
 */
const FONTEDITOR_OPTIONS = { hinting: true, kerning: true };

function tagAt(bytes: Uint8Array, offset: number): string {
    let tag = '';
    for (let i = offset; i < offset + 4; i += 1) tag += String.fromCharCode(bytes[i]);
    return tag;
}

function isTrueTypeVersion(bytes: Uint8Array, offset = 0): boolean {
    if (bytes[offset] === 0x00 && bytes[offset + 1] === 0x01
        && bytes[offset + 2] === 0x00 && bytes[offset + 3] === 0x00) return true;
    return TRUETYPE_TAGS.indexOf(tagAt(bytes, offset)) !== -1;
}

/**
 * Identify a font by its bytes. The From-format dropdown is a page/SEO concern;
 * what we convert is whatever the user actually uploaded.
 */
export function sniffFontFormat(bytes: Uint8Array): SourceFormat {
    if (!bytes || bytes.length < 8) return 'unknown';
    const tag = tagAt(bytes, 0);
    if (tag === 'wOFF') return 'woff';
    if (tag === 'wOF2') return 'woff2';
    if (tag === OTTO) return 'otf';
    if (tag === 'ttcf') return 'ttc';
    if (isTrueTypeVersion(bytes)) return 'ttf';
    if (bytes.length > EOT_MAGIC_OFFSET + 1
        && bytes[EOT_MAGIC_OFFSET] === 0x4c && bytes[EOT_MAGIC_OFFSET + 1] === 0x50) return 'eot';
    return 'unknown';
}

/** Outline format of a raw sfnt, or null when the bytes are not an sfnt. */
export function sfntFlavour(sfnt: Uint8Array): SfntFlavour | null {
    if (!sfnt || sfnt.length < 12) return null;
    if (tagAt(sfnt, 0) === OTTO) return 'cff';
    if (isTrueTypeVersion(sfnt)) return 'truetype';
    return null;
}

/** Four-character tags in an sfnt's table directory (used for capability warnings). */
export function sfntTableTags(sfnt: Uint8Array): string[] {
    if (!sfnt || sfnt.length < 12) return [];
    const numTables = (sfnt[4] << 8) | sfnt[5];
    const tags: string[] = [];
    for (let i = 0; i < numTables; i += 1) {
        const offset = 12 + i * 16;
        if (offset + 4 > sfnt.length) break;
        tags.push(tagAt(sfnt, offset));
    }
    return tags;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    const buffer = bytes.buffer as ArrayBuffer;
    if (bytes.byteOffset === 0 && bytes.byteLength === buffer.byteLength) return buffer;
    return buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

/** Normalise whatever a font library hands back (Buffer / ArrayBuffer / view). */
function asBytes(value: any): Uint8Array {
    if (value instanceof Uint8Array) return value;
    if (value instanceof ArrayBuffer) return new Uint8Array(value);
    if (value && value.buffer instanceof ArrayBuffer) {
        return new Uint8Array(value.buffer, value.byteOffset || 0, value.byteLength);
    }
    throw new Error('Font conversion produced no data.');
}

/** CJS/ESM interop: webpack hands back { default }, jest sometimes the export itself. */
function unwrap(mod: any): any {
    return (mod && mod.default) || mod;
}

// ---------------------------------------------------------------------------
// Library loaders — every one of these is dynamic on purpose (see file header).
// ---------------------------------------------------------------------------

/**
 * wawoff2 ships two independent ~1 MB emscripten bindings (the Brotli encoder
 * and decoder, each with its wasm inlined as a data: URI). Import the exact
 * binding so a WOFF2 -> TTF page never downloads the encoder, and a
 * TTF -> WOFF2 page never downloads the decoder.
 *
 * The bindings are evaluated from source with an injected `Module` (the
 * standard emscripten pattern) rather than imported as CommonJS: wawoff2's
 * `module.exports = Module` only runs inside its node-environment branch, so
 * in a browser bundle `require('wawoff2/decompress')` hands back an empty
 * object, its wrapper's onRuntimeInitialized hook lands on nothing, and every
 * WOFF2 encode/decode hangs forever with no error. Measured on QA 2026-09-01
 * by instrumenting the built chunks; the earlier `import('wawoff2')` had the
 * same defect, so TTF -> WOFF2 had never worked in a browser either.
 * next.config.js serves the two binding files as `asset/source` for this.
 */
type EmscriptenFn = (bytes: Uint8Array) => Uint8Array | false;
const woff2Engines: Partial<Record<'compress' | 'decompress', Promise<EmscriptenFn>>> = {};

async function loadWoff2Engine(kind: 'compress' | 'decompress'): Promise<EmscriptenFn> {
    if (!woff2Engines[kind]) {
        woff2Engines[kind] = (async () => {
            const src: string = unwrap(
                kind === 'decompress'
                    ? await import('wawoff2/build/decompress_binding.js')
                    : await import('wawoff2/build/compress_binding.js'),
            );
            const Module: any = {};
            const ready = new Promise<void>((resolve, reject) => {
                Module.onRuntimeInitialized = resolve;
                Module.onAbort = (why: unknown) => reject(new Error(`The WOFF2 engine failed to start (${String(why)}).`));
            });
            // The emscripten prologue is `var Module = typeof Module !== 'undefined' ? Module : {}`,
            // so a parameter named Module is picked up as the runtime's configuration object.
            // eslint-disable-next-line no-new-func
            new Function('Module', src)(Module);
            let timer: ReturnType<typeof setTimeout> | undefined;
            await Promise.race([
                ready,
                new Promise<never>((_, reject) => {
                    timer = setTimeout(() => reject(new Error('The WOFF2 engine did not start in time. Please try again.')), 30000);
                }),
            ]).finally(() => { if (timer) clearTimeout(timer); });
            if (typeof Module[kind] !== 'function') throw new Error('The WOFF2 engine started without its entry point.');
            return Module[kind] as EmscriptenFn;
        })().catch((e) => {
            delete woff2Engines[kind];
            throw e;
        });
    }
    return woff2Engines[kind]!;
}

async function loadWoff2Decompress(): Promise<(b: Uint8Array) => Promise<Uint8Array>> {
    const decompress = await loadWoff2Engine('decompress');
    return async (b) => {
        const out = decompress(b);
        if (out === false) throw new Error('This WOFF2 file could not be decompressed — it may be damaged or not a WOFF2 font.');
        return out;
    };
}

async function loadWoff2Compress(): Promise<(b: Uint8Array) => Promise<Uint8Array>> {
    const compress = await loadWoff2Engine('compress');
    return async (b) => {
        const out = compress(b);
        if (out === false) throw new Error('This font could not be compressed to WOFF2.');
        return out;
    };
}

interface FonteditorCore {
    otf2ttfobject: any;
    TTFReader: any;
    TTFWriter: any;
    woff2ttf: any;
    eot2ttf: any;
}

/**
 * Deep imports into fonteditor-core/lib/* rather than the package root: the
 * root entry also pulls in the SVG font reader (@xmldom/xmldom) and the
 * package's own WOFF2 wasm glue, which we do not use — 286 KB vs 100 KB
 * minified. The `./lib/*` subpath is part of the package's exports map.
 */
async function loadFonteditorCore(): Promise<FonteditorCore> {
    const [otf2ttfobject, ttfreader, ttfwriter, woff2ttf, eot2ttf] = await Promise.all([
        import('fonteditor-core/lib/ttf/otf2ttfobject'),
        import('fonteditor-core/lib/ttf/ttfreader'),
        import('fonteditor-core/lib/ttf/ttfwriter'),
        import('fonteditor-core/lib/ttf/woff2ttf'),
        import('fonteditor-core/lib/ttf/eot2ttf'),
    ]);
    return {
        otf2ttfobject: unwrap(otf2ttfobject),
        TTFReader: unwrap(ttfreader),
        TTFWriter: unwrap(ttfwriter),
        woff2ttf: unwrap(woff2ttf),
        eot2ttf: unwrap(eot2ttf),
    };
}

/** pako/lib/inflate.js, not the pako root: ttf2woff already pulls the sibling
 *  deflate entry, so the WOFF paths share one copy of the zlib code. */
async function loadInflate(): Promise<(b: Uint8Array) => Uint8Array> {
    const mod: any = await import('pako/lib/inflate.js');
    return (mod.inflate || (mod.default && mod.default.inflate)) as (b: Uint8Array) => Uint8Array;
}

// ---------------------------------------------------------------------------
// Container decode / encode
// ---------------------------------------------------------------------------

export interface FontLibs {
    woff2ToSfnt(bytes: Uint8Array): Promise<Uint8Array>;
    woffToSfnt(bytes: Uint8Array): Promise<Uint8Array>;
    eotToSfnt(bytes: Uint8Array): Promise<Uint8Array>;
    sfntToWoff(sfnt: Uint8Array): Promise<Uint8Array>;
    sfntToWoff2(sfnt: Uint8Array): Promise<Uint8Array>;
    sfntToEot(sfnt: Uint8Array): Promise<Uint8Array>;
    cffToTrueType(sfnt: Uint8Array): Promise<Uint8Array>;
    trueTypeToCff(sfnt: Uint8Array): Promise<{ sfnt: Uint8Array; droppedLayoutTables: boolean }>;
}

export const defaultFontLibs: FontLibs = {
    async woff2ToSfnt(bytes) {
        const decompress = await loadWoff2Decompress();
        return asBytes(await decompress(bytes));
    },

    async woffToSfnt(bytes) {
        const [{ woff2ttf }, inflate] = await Promise.all([loadFonteditorCore(), loadInflate()]);
        return asBytes(woff2ttf(toArrayBuffer(bytes), { inflate }));
    },

    async eotToSfnt(bytes) {
        const { eot2ttf } = await loadFonteditorCore();
        return asBytes(eot2ttf(toArrayBuffer(bytes), FONTEDITOR_OPTIONS));
    },

    async sfntToWoff(sfnt) {
        const ttf2woff = unwrap(await import('ttf2woff'));
        // ttf2woff rewrites head.checkSumAdjustment in place, so give it a copy:
        // `sfnt` can be the user's own uploaded buffer. — 2026-09-01
        return withWoffFlavour(asBytes(ttf2woff(new Uint8Array(sfnt))), sfnt);
    },

    async sfntToWoff2(sfnt) {
        const compress = await loadWoff2Compress();
        return asBytes(await compress(sfnt));
    },

    async sfntToEot(sfnt) {
        const ttf2eot = unwrap(await import('ttf2eot'));
        return asBytes(ttf2eot(sfnt));
    },

    async cffToTrueType(sfnt) {
        const { otf2ttfobject, TTFWriter } = await loadFonteditorCore();
        const ttfObject = otf2ttfobject(toArrayBuffer(sfnt), FONTEDITOR_OPTIONS);
        return asBytes(new TTFWriter(FONTEDITOR_OPTIONS).write(ttfObject));
    },

    async trueTypeToCff(sfnt) {
        const opentype: any = unwrap(await import('opentype.js'));
        const font = opentype.parse(toArrayBuffer(sfnt));
        ensureEnglishNames(font);
        try {
            return { sfnt: asBytes(font.toArrayBuffer()), droppedLayoutTables: false };
        } catch (first: any) {
            // opentype.js's GSUB/GPOS writer only covers a subset of the lookup
            // types real fonts use ("Unable to write GSUB lookup type 7 tables",
            // "lookup type 6 format 2 is not yet supported"). Measured over 2,000
            // system fonts: 481 fail the first write and all but one write
            // cleanly once the layout tables are dropped. Losing the layout
            // features is worth telling the user about (LAYOUT_DROPPED_WARNING)
            // but far better than failing the conversion outright. — 2026-09-01
            font.tables.gsub = undefined;
            font.tables.gpos = undefined;
            font.tables.gdef = undefined;
            try {
                return { sfnt: asBytes(font.toArrayBuffer()), droppedLayoutTables: true };
            } catch (second: any) {
                throw new Error(
                    `This font could not be re-encoded with PostScript (CFF) outlines: ${second.message || first.message}`
                );
            }
        }
    },
};

/**
 * opentype.js's writer reads the family/style/PostScript names through
 * getEnglishName() and crashes on `undefined.replace` when a font carries no
 * English name record (common on Indic and CJK fonts). Backfill `.en` from any
 * other language present before writing. — 2026-09-01
 */
function ensureEnglishNames(font: any): void {
    const names = font && font.names;
    if (!names) return;
    const primary = names.unicode || names.macintosh || names.windows;
    if (!primary) return;
    const tables = [names.unicode, names.macintosh, names.windows];
    ['fontFamily', 'fontSubfamily', 'postScriptName'].forEach((key) => {
        const current = primary[key];
        if (current && typeof current.en === 'string' && current.en.trim()) return;
        let fallback: string | undefined;
        tables.forEach((table) => {
            if (fallback || !table || !table[key]) return;
            const entry = table[key];
            fallback = entry.en || Object.keys(entry)
                .map((lang) => entry[lang])
                .find((value: any) => typeof value === 'string' && value.trim());
        });
        if (fallback) primary[key] = Object.assign({}, current || {}, { en: fallback });
    });
}

/**
 * ttf2woff writes the WOFF `flavor` field from head.fontRevision instead of the
 * sfnt version, so every WOFF it produces claims to be TrueType — including the
 * ones wrapping CFF outlines. The spec requires flavor to equal the sfnt
 * version, so correct it from the source sfnt. — 2026-09-01
 */
export function withWoffFlavour(woff: Uint8Array, sfnt: Uint8Array): Uint8Array {
    if (woff.length < 8 || tagAt(woff, 0) !== 'wOFF') return woff;
    const cff = sfntFlavour(sfnt) === 'cff';
    const flavour = cff ? [0x4f, 0x54, 0x54, 0x4f] : [0x00, 0x01, 0x00, 0x00];
    const patched = new Uint8Array(woff);
    patched.set(flavour, 4);
    return patched;
}

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

export const MIME_TYPES: Record<FontFormat, string> = {
    ttf: 'font/ttf',
    otf: 'font/otf',
    woff: 'font/woff',
    woff2: 'font/woff2',
    eot: 'application/vnd.ms-fontobject',
};

const CFF_TO_TRUETYPE_WARNING =
    'PostScript (CFF) outlines were converted to TrueType outlines. '
    + 'The shapes are preserved, but GSUB layout features (ligatures, stylistic alternates) are not carried over.';

const LAYOUT_DROPPED_WARNING =
    'This font uses OpenType layout tables that cannot be re-encoded in the browser, '
    + 'so GSUB/GPOS features (ligatures, contextual alternates, kerning) were left out of the converted font.';

const VARIABLE_FONT_WARNING =
    'This is a variable font. The converted file is a single static instance — '
    + 'the variation axes are not carried over.';

export interface ConversionResult {
    data: Uint8Array;
    mimeType: string;
    /** What the input actually was, by signature. */
    source: SourceFormat;
    /** Outline format of the decoded sfnt. */
    flavour: SfntFlavour;
    /** Lossy-conversion notice to show the user, if any. */
    warning: string | null;
}

/**
 * Decode any supported container down to the raw sfnt it carries.
 * Pass-through for TTF/OTF, which already are sfnt.
 */
export async function decodeToSfnt(
    bytes: Uint8Array,
    source: SourceFormat,
    libs: FontLibs = defaultFontLibs,
): Promise<Uint8Array> {
    switch (source) {
        case 'woff2': return libs.woff2ToSfnt(bytes);
        case 'woff': return libs.woffToSfnt(bytes);
        case 'eot': return libs.eotToSfnt(bytes);
        default: return bytes;
    }
}

export async function convertFont(
    input: Uint8Array,
    target: FontFormat,
    libs: FontLibs = defaultFontLibs,
): Promise<ConversionResult> {
    const source = sniffFontFormat(input);
    if (source === 'ttc') {
        throw new Error('TrueType Collections (.ttc) hold several fonts in one file and are not supported. Extract a single font first.');
    }
    if (source === 'unknown') {
        throw new Error('This file is not a font we recognise. Supported inputs are TTF, OTF, WOFF, WOFF2 and EOT.');
    }

    let sfnt: Uint8Array;
    try {
        sfnt = await decodeToSfnt(input, source, libs);
    } catch (e: any) {
        throw new Error(`This ${source.toUpperCase()} file could not be read: ${e && e.message ? e.message : e}`);
    }

    const flavour = sfntFlavour(sfnt);
    if (!flavour) {
        throw new Error(`This ${source.toUpperCase()} file does not contain a readable font.`);
    }

    const warnings: string[] = [];
    const tables = sfntTableTags(sfnt);
    const isVariable = tables.indexOf('fvar') !== -1;
    let data: Uint8Array;

    if (target === 'ttf') {
        if (flavour === 'cff' && tables.indexOf('CFF2') !== -1) {
            // CFF2 is the variable-font flavour of PostScript outlines; nothing
            // in the browser stack converts it to glyf. Say so instead of
            // failing with a library-internal message. — 2026-09-01
            throw new Error('This is a variable OpenType font with CFF2 outlines, which cannot be converted to TrueType in the browser. Convert it to WOFF or WOFF2 instead — those keep the font exactly as it is.');
        }
        if (flavour === 'truetype') {
            // Already TrueType-flavoured: hand back the sfnt untouched. Note the
            // legacy 'true'/'typ1' sfnt versions are left alone rather than
            // rewritten to 0x00010000 — both are valid TrueType versions and
            // rewriting the tag would invalidate head.checkSumAdjustment.
            // — 2026-09-01
            data = sfnt;
        } else {
            let produced: Uint8Array | null = null;
            let reason = '';
            try {
                produced = await libs.cffToTrueType(sfnt);
            } catch (e: any) {
                produced = null;
                if (e && e.message) reason = e.message;
            }
            // Never ship a CFF font under a .ttf name (that was the old bug):
            // if the outline conversion did not produce a TrueType-flavoured
            // sfnt, fail and say why. — 2026-09-01
            if (!produced || sfntFlavour(produced) !== 'truetype') {
                throw new Error(`This font's PostScript (CFF) outlines could not be converted to TrueType outlines${reason ? ` (${reason})` : ''}. Convert it to OTF, WOFF or WOFF2 instead — those keep the original outlines.`);
            }
            data = produced;
            warnings.push(CFF_TO_TRUETYPE_WARNING);
            if (isVariable) warnings.push(VARIABLE_FONT_WARNING);
        }
    } else if (target === 'otf') {
        if (flavour === 'cff') {
            data = sfnt;
        } else {
            let result: { sfnt: Uint8Array; droppedLayoutTables: boolean } | null = null;
            let reason = '';
            try {
                result = await libs.trueTypeToCff(sfnt);
            } catch (e: any) {
                result = null;
                if (e && e.message) reason = e.message;
            }
            if (!result || sfntFlavour(result.sfnt) !== 'cff') {
                throw new Error(`This font's TrueType outlines could not be converted to PostScript (CFF) outlines${reason ? ` (${reason})` : ''}. Convert it to TTF, WOFF or WOFF2 instead — those keep the original outlines.`);
            }
            data = result.sfnt;
            if (result.droppedLayoutTables) warnings.push(LAYOUT_DROPPED_WARNING);
            if (isVariable) warnings.push(VARIABLE_FONT_WARNING);
        }
    } else if (target === 'woff') {
        data = await libs.sfntToWoff(sfnt);
    } else if (target === 'woff2') {
        data = await libs.sfntToWoff2(sfnt);
    } else if (target === 'eot') {
        // EOT is a container: it wraps whatever sfnt it is given, so the outline
        // format is preserved rather than forced to TrueType. — 2026-09-01
        data = await libs.sfntToEot(sfnt);
    } else {
        throw new Error(`Unsupported output format: ${target}`);
    }

    if (!data || !data.length) throw new Error('Conversion produced an empty file.');

    return {
        data,
        mimeType: MIME_TYPES[target],
        source,
        flavour,
        warning: warnings.length ? warnings.join(' ') : null,
    };
}
